import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Pill,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePagePanel,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { useDrawer, usePersona } from '@/app/providers';
import { Commentable } from '@/shared/comments/Commentable';
import { useAdvisoryFlowRead } from '@/versions/v1/shared/useAdvisoryFlowRead';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { INITIAL_STATE } from '@/versions/v1/shared/ScenarioEngine';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { DEFAULT_CURSOR, type SeriesTime } from '@/versions/v1/schema/reading-series';
import { TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import { formatCaseClock } from '@/versions/v1/shared/clock.derive';
import { controlLoops } from '@/versions/v1/modules/process/Scene.transform';
import {
  advisoryCopy,
  approvalTabForReview,
  caseById,
  enrichFmeaReview,
  enrichFmeaReviews,
  filterReviewsByTab,
  alarmsWithSession,
  dcsAlarmsForPanel,
  type ApprovalTab,
} from './Scene.transform';
import { approvalsRuleSetPanel } from './Approvals.transform';
import { APPROVAL_GATE_SEAT } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { RoleScreen } from './RoleScreen';
import { roleScreen, roleScreenForPersona } from './RoleScreen.transform';
import { RuleSetPanel } from './RuleSetPanel';
import { LearnBody, ValueBody } from './LateStageBodies';
import { eventClock, learnRuleSetRead, learnStateLabel } from './LateStageBodies.transform';
import { fmeaLearningModes } from './FmeaLearning.transform';
import { FlowLanding } from './FlowLanding';
import { ExcursionsBody, PlantDataBody, SignalsBody } from './EarlyStageBodies';
import { ActionBody, DiagnosisBody, NoBreakPlannedStop } from './MidStageBodies';
import { advisoryShowsMoney, casesTableMeta, casesTableRows } from './MidStageBodies.transform';
import { subprocessName } from './EarlyStageBodies.transform';
import { FmeaLearningUpdates } from './FmeaLearning';
import type { PipelineActor, PipelineCellRead, PipelineStageId } from './PipelineStrip.transform';
import { ActorChip } from './PipelineStrip';
import { PIPELINE_STAGES } from './PipelineStrip.transform';
import { stageForAdvisoryLocation } from './PipelineStrip.transform';
import {
  buildFlowReplaySchedule,
  flowClockAtOffset,
  flowClockOffset,
  replayOffsetAtElapsed,
  replayStepOffset,
  ZINC_REPLAY_END,
  ZINC_REPLAY_START,
  type FlowLandingRead,
} from './FlowLanding.transform';
import { valueBandRead } from './ValueBand.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

function CaseStageProgress({ stageIndex, stage }: { stageIndex: number; stage: PipelineStageId }) {
  return (
    <div className="min-w-0">
      <span
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={PIPELINE_STAGES.length}
        aria-valuenow={stageIndex + 1}
        aria-label={`${PIPELINE_STAGES[stageIndex]?.label ?? 'Cases'}, stage ${stageIndex + 1} of ${PIPELINE_STAGES.length}`}
        className="inline-flex shrink-0 items-center gap-[3px]"
      >
        {PIPELINE_STAGES.map((row, index) => (
          <span
            key={row.id}
            title={row.label}
            className={`h-[5px] w-3 rounded-full ${
              index === stageIndex
                ? 'bg-[var(--color-accent-500)]'
                : index < stageIndex
                  ? 'bg-[var(--ink-tertiary)]'
                  : 'bg-[var(--hairline-strong)]'
            }`}
          />
        ))}
      </span>
      <span className="mt-1 block whitespace-nowrap text-xs text-ink-secondary">
        {PIPELINE_STAGES.find((row) => row.id === stage)?.label}
        <span className="text-muted"> · {stageIndex + 1} of {PIPELINE_STAGES.length}</span>
      </span>
    </div>
  );
}

const FEED_ACTOR: Readonly<Record<FlowLandingRead['shiftFeed'][number]['actor'], PipelineActor>> = {
  Engine: 'Engine',
  Agent: 'Agent',
  Human: 'Human',
};

function CasesFlowPanels({
  read,
  projectPath,
  caseCodeById,
  trackedCaseId,
  showMoney = true,
  onOpenCase,
}: {
  read: FlowLandingRead;
  projectPath: string;
  caseCodeById: ReadonlyMap<string, string>;
  /** The case the strip's marker follows; its row carries the selected tab's fill. */
  trackedCaseId: string | null;
  /** Comp adjudication 7 (polish.P28b): the floor personas read the value path, never US$. */
  showMoney?: boolean;
  onOpenCase: (caseId: string) => void;
}) {
  return (
    <div className="grid grid-cols-12 gap-3">
      <div className="col-span-12 lg:col-span-8">
        <Commentable name="Flow Cases">
          <SurfacePagePanel
            title={showMoney ? `Every open case · where it is · by ${CURRENCY_GLYPH}/day` : 'Every open case · where it is'}
            meta={casesTableMeta(read.casesStageRows.length, showMoney)}
            actions={read.showRecoveryFilter ? (
              <div className="flex items-center gap-2">
                <Pill tone="warning">Recovery filter</Pill>
                <Link to={`${projectPath}/advisory/cases`} className="text-xs font-medium text-accent-700 hover:underline dark:text-accent-400">
                  clear
                </Link>
              </div>
            ) : undefined}
            flush
          >
            <div className="max-h-[20rem] overflow-auto">
              {/* #740: the five columns fit the card at 1280 (616 px) with no
                  sideways scroll — the case title wraps (no min-width floor on
                  its column) and the owner chip runs to a second line rather
                  than truncating, so no figure or name hides behind a swipe. */}
              <Table clickableRows wrap={false} className="bg-[var(--surface-2)]">
                <THead className="sticky top-0 z-10 bg-[var(--surface-2)]">
                  <TR>
                    <TH className="whitespace-nowrap px-4 py-2 text-xs">Case · age</TH>
                    <TH className="whitespace-nowrap px-1.5 py-2 text-xs">Circuit</TH>
                    <TH className="whitespace-nowrap px-1.5 py-2 text-xs">Where it is</TH>
                    <TH className="whitespace-nowrap px-1.5 py-2 text-xs">Who acts</TH>
                    <TH className="whitespace-nowrap px-1.5 py-2 text-right text-xs">{showMoney ? `${CURRENCY_GLYPH}/day · value path` : 'Value path'}</TH>
                  </TR>
                </THead>
                <TBody data-open-case-list>
                  {casesTableRows(read.openCaseRows, showMoney).map((row) => (
                    <TR
                      key={row.caseItem.id}
                      data-case-stage={row.stage}
                      data-flow-case={row.caseItem.id}
                      data-tracked-case={row.caseItem.id === trackedCaseId || undefined}
                      className={row.caseItem.id === trackedCaseId
                        ? 'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))] hover:bg-[color-mix(in_srgb,var(--color-accent-500)_12%,var(--surface-2))] dark:hover:bg-[color-mix(in_srgb,var(--color-accent-500)_12%,var(--surface-2))]'
                        : undefined}
                      onRowClick={() => onOpenCase(row.caseItem.id)}
                    >
                      <TD className="px-4 py-2">
                        {/* The 14rem measure bounds the title column's share of the
                            card at 1470, so the longest meta line still wraps to two
                            lines and a daily row keeps the one-off bearing row's
                            height (K18). The column itself takes its natural
                            minimum (102.6 px) at 1280, so the measure never forces
                            the tile to scroll. */}
                        <div className="max-w-[14rem] text-sm font-medium leading-snug text-ink-primary">{row.caseItem.title}</div>
                        <div className="mt-0.5 max-w-[14rem] font-mono text-[10px] text-muted">
                          {row.caseItem.code ? `${row.caseItem.code} · ` : ''}raised {row.age} ago
                        </div>
                      </TD>
                      <TD className="px-1.5 py-2 text-xs text-ink-secondary">{row.caseItem.circuit}</TD>
                      <TD className="px-1.5 py-2"><CaseStageProgress stageIndex={row.stageIndex} stage={row.stage} /></TD>
                      <TD className="px-1.5 py-2"><ActorChip actor="Human" wrap>{row.actor}</ActorChip></TD>
                      <TD className="w-36 min-w-32 whitespace-normal py-2 pl-1 pr-1 text-right tabular">
                        {showMoney && row.money ? (
                          <>
                            {/* D17 addendum: a contained share reads muted, as part of its owner's
                                counted amount rather than a second figure added to the strip. */}
                            <div className={`${row.dailyLossPerDay === null ? 'whitespace-normal' : 'whitespace-nowrap'} text-sm font-semibold ${row.containedWithin ? 'text-muted' : 'text-ink-primary'}`} data-case-row-value>{row.money}</div>
                            {/* K7 r4: keep `within <owner>` whole so the owner code never
                                orphans on its own line; the row stays at two lines. */}
                            {/* D16/comp: the conditional pair is secondary text, read at the
                                same rhythm as a daily row's basis so a one-off figure never
                                out-weighs a counted one. */}
                            {/* K18: the pair keeps its pre-D26 four-line measure, so the
                                one-off row still equals the two-line daily row after the wider US$ glyph. */}
                            <div className={`mt-0.5 text-[10px] text-muted ${row.dailyLossPerDay === null ? 'ml-auto max-w-[122px] leading-[1.315]' : 'leading-snug'}`}>
                              {row.containedWithin && row.moneyComparison.includes('within ') ? (
                                <>
                                  {row.moneyComparison.slice(0, row.moneyComparison.indexOf('within '))}
                                  <span className="whitespace-nowrap">within {row.containedWithin}</span>
                                </>
                              ) : <NoBreakPlannedStop text={row.moneyComparison} />}
                            </div>
                          </>
                        ) : (
                          <div className="text-xs leading-snug text-muted">Feeds {row.feedLabel}</div>
                        )}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </SurfacePagePanel>
        </Commentable>
      </div>

      <div className="col-span-12 lg:col-span-4">
        <Commentable name="Shift feed">
          <SurfacePagePanel title="Recent case events" contentClassName="py-1">
            <ul className="max-h-[18rem] divide-y divide-divider overflow-y-auto">
              {read.shiftFeed.map((event, index) => {
                const code = caseCodeById.get(event.caseId);
                const lead = code && event.detail.startsWith(code) ? code : null;
                const split = event.timeLabel.lastIndexOf(' ');
                return (
                  <li key={`${event.at}-${event.actor}-${index}`} className="grid min-w-0 grid-cols-[3.25rem_minmax(0,1fr)] items-start gap-2.5 py-2">
                    <time className="pt-0.5 font-mono text-xs leading-tight tabular-nums text-muted">
                      {event.timeLabel.slice(split + 1)}
                      <span className="block text-[10px]">{event.timeLabel.slice(0, split)}</span>
                    </time>
                    <p className="min-w-0 text-xs leading-relaxed text-ink-secondary">
                      <ActorChip actor={FEED_ACTOR[event.actor]} />{' '}
                      {lead ? (
                        <>
                          <span className="font-semibold text-ink-primary">{lead}</span>
                          {event.detail.slice(lead.length)}
                        </>
                      ) : event.detail}
                      {event.repeatCount > 1 ? ` ×${event.repeatCount}` : ''}
                    </p>
                  </li>
                );
              })}
            </ul>
          </SurfacePagePanel>
        </Commentable>
      </div>
    </div>
  );
}

/**
 * Until every case fixture's frames are in hand, the strip shows no figure at
 * all — a cold load must never paint the pre-frame seed state on its way to the
 * cursor's state.
 */
const NEUTRAL_CELLS = Object.fromEntries(
  PIPELINE_STAGES.map((stage) => [stage.id, { count: '', comparison: '' }]),
) as Readonly<Record<PipelineStageId, PipelineCellRead>>;

export function AdvisoryScene() {
  const { projectId, tab: tabPathParam } = useParams<{ projectId: string; tab?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const project = projectTileBySlug(projectId);
  const plantName = project?.name ?? 'this plant';
  const copy = advisoryCopy(plantName);
  const { persona } = usePersona();
  const { can } = useRole();
  // #731: one module-wide money guard — the floor seats (Operator, Control
  // room) read no US$ on the band, the strip, the stage bodies or the case page.
  const showMoney = advisoryShowsMoney(persona);
  const { open: openDrawer } = useDrawer();
  const { state, setCursor } = useScenario();
  const advisory = useAdvisory();
  // #778: the strip, the project home's Advisory row and the rail badge read one
  // engine read. `useAdvisoryFlowRead` owns the input assembly; the route only
  // adds the inputs its own stages render (investigation runs, outcome filter,
  // the floor seats' money guard). A case whose milestones come from frames is
  // not placed until its frames are in hand, so `read` waits for them — the same
  // gate the strip paints its own figures behind.
  const {
    read: flowRead,
    replayCaseId,
    diagnosis,
    cases,
    casesForDisplay,
    pipelineCases,
    actions,
    reviews: fmeaReviews,
    stageTimelines,
  } = useAdvisoryFlowRead({
    withInvestigationRuns: true,
    outcomeFilter: searchParams.get('outcome'),
    showMoney,
  });

  const [approvalTab, setApprovalTab] = useState<ApprovalTab>('to-review');
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);

  const reviewParam = searchParams.get('review') || searchParams.get('hasReview') || null;
  const causeParam = searchParams.get('cause');

  const stage = useMemo(
    () => stageForAdvisoryLocation({
      pathTab: tabPathParam,
      queryTab: searchParams.get('tab'),
      hasReview: Boolean(reviewParam || searchParams.has('review') || searchParams.has('hasReview')),
      hasCause: Boolean(causeParam),
    }),
    [tabPathParam, searchParams, reviewParam, causeParam],
  );

  const changeStage = (nextStage: PipelineStageId) => {
    const nextSearch = new URLSearchParams(searchParams);
    nextSearch.delete('tab');
    if (nextStage !== 'cases') nextSearch.delete('outcome');
    if (nextStage !== 'learn') nextSearch.delete('review');
    if (nextStage !== 'learn') nextSearch.delete('cause');
    const suffix = nextSearch.toString();
    navigate(`/projects/${projectId}/advisory/${nextStage}${suffix ? `?${suffix}` : ''}`);
  };

  useEffect(() => {
    if (!reviewParam) return;
    setSelectedReviewId(reviewParam);
  }, [reviewParam]);

  useEffect(() => {
    document.title = project ? `${copy.title} · ${project.name}` : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  useEffect(() => {
    const review = reviewParam ? fmeaReviews.find((row) => row.id === reviewParam) : undefined;
    if (!review) return;
    setApprovalTab(approvalTabForReview(review));
  }, [reviewParam, fmeaReviews]);

  const alarms = useMemo(() => alarmsWithSession(advisory, state), [advisory.ackedAlarmIds, state]);
  const dcsAlarms = useMemo(() => dcsAlarmsForPanel(alarms).visible, [alarms]);
  const loops = useMemo(() => controlLoops(), []);
  const scopeParam = searchParams.get('scope');
  const roleScreenCases = flowRead?.roleScreenCases ?? [];
  const displayCases = flowRead?.displayCases ?? [];
  const valueRead = useMemo(
    () => valueBandRead({ ...INITIAL_STATE, cursor: DEFAULT_CURSOR }, projectId),
    [projectId],
  );
  const caseCodeById = useMemo(
    () => new Map(pipelineCases.flatMap((caseItem) => (caseItem.code ? [[caseItem.id, caseItem.code] as const] : []))),
    [pipelineCases],
  );
  const replayTicks = useMemo(
    () => buildFlowReplaySchedule(ZINC_REPLAY_START, stageTimelines.get(replayCaseId ?? '')).dwells.map((event) => event.offset),
    [stageTimelines, replayCaseId],
  );

  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replayHolding, setReplayHolding] = useState(false);
  const zincStage = replayCaseId && flowRead ? flowRead.caseStages.get(replayCaseId) ?? null : null;
  const previousZincStage = useRef<PipelineStageId | null>(null);
  const [arrivingStage, setArrivingStage] = useState<PipelineStageId | null>(null);
  const replayStarted = useRef(false);
  const replayOffsetOnStart = useRef(0);
  useEffect(() => {
    const nextStage = zincStage;
    if (nextStage && replayStarted.current && replayPlaying && previousZincStage.current && previousZincStage.current !== nextStage) {
      setArrivingStage(nextStage);
      const timer = window.setTimeout(() => setArrivingStage(null), 950);
      previousZincStage.current = nextStage;
      return () => window.clearTimeout(timer);
    }
    previousZincStage.current = nextStage;
    return undefined;
  }, [zincStage, replayPlaying]);

  useEffect(() => {
    if (!replayPlaying) return undefined;
    const maxOffset = flowClockOffset(ZINC_REPLAY_END);
    const startCursor = flowClockAtOffset(replayOffsetOnStart.current);
    const schedule = buildFlowReplaySchedule(startCursor, stageTimelines.get(replayCaseId ?? ''));
    const startedAt = performance.now();
    let frame = 0;
    let lastStep = -1;
    let lastHoldOffset = -1;
    const tick = (now: number) => {
      const elapsed = now - startedAt;
      const rawOffset = replayOffsetAtElapsed(schedule, elapsed);
      const heldEvent = schedule.dwells.find((event) => Math.abs(event.offset - rawOffset) < 0.0001);
      const nextCursor = heldEvent?.at;
      const nextStep = elapsed >= schedule.durationMs
        ? maxOffset
        : heldEvent?.offset ?? replayStepOffset(rawOffset, lastStep);
      const holdOffset = heldEvent?.offset ?? -1;
      if (holdOffset !== lastHoldOffset) {
        lastHoldOffset = holdOffset;
        setReplayHolding(heldEvent !== undefined);
      }
      if (nextStep !== lastStep) {
        lastStep = nextStep;
        replayOffsetOnStart.current = nextStep;
        setCursor(nextCursor ?? flowClockAtOffset(nextStep));
      }
      if (elapsed >= schedule.durationMs) {
        setReplayPlaying(false);
        setReplayHolding(false);
        return;
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [replayPlaying, setCursor, stageTimelines, replayCaseId]);

  const toggleReplay = () => {
    if (replayPlaying) {
      setReplayPlaying(false);
      setReplayHolding(false);
      return;
    }
    if (state.cursor < ZINC_REPLAY_START || state.cursor >= ZINC_REPLAY_END) {
      setCursor(ZINC_REPLAY_START);
      replayOffsetOnStart.current = 0;
    } else {
      replayOffsetOnStart.current = flowClockOffset(state.cursor);
    }
    replayStarted.current = true;
    setReplayPlaying(true);
  };

  const scrubReplay = (cursor: SeriesTime) => {
    setReplayPlaying(false);
    setReplayHolding(false);
    replayOffsetOnStart.current = flowClockOffset(cursor);
    setCursor(cursor);
  };

  const caseParam = searchParams.get('case');
  useEffect(() => {
    if (!caseParam) return;
    const caseItem = caseById(casesForDisplay, caseParam);
    if (!caseItem) return;
    openDrawer('case-drawer', {
      kicker: caseItem.code ?? 'Case',
      title: caseItem.title,
      wide: true,
      data: { caseId: caseParam },
    });
  }, [caseParam, casesForDisplay, openDrawer]);

  const roleScreenModel = useMemo(
    () =>
      roleScreen(roleScreenForPersona(persona), {
        cases: roleScreenCases,
        reviews: fmeaReviews,
        loops,
        actions,
        alarms,
        scope: scopeParam,
        sessionState: state,
        advisory,
        diagnosis,
      }),
    [persona, roleScreenCases, fmeaReviews, loops, actions, alarms, scopeParam, state, advisory, diagnosis],
  );

  const approvalPanel = useMemo(
    () => approvalsRuleSetPanel(advisory, state.cursor),
    [advisory, state.cursor],
  );
  const learnRuleSet = useMemo(() => learnRuleSetRead(advisory), [advisory]);

  const approvalRows = useMemo(
    () => enrichFmeaReviews(filterReviewsByTab(fmeaReviews, approvalTab), cases),
    [fmeaReviews, approvalTab, cases],
  );
  const selectedReview = useMemo(() => {
    if (approvalRows.length === 0) return undefined;
    const review = selectedReviewId
      ? fmeaReviews.find((row) => row.id === selectedReviewId)
      : undefined;
    if (!review) return approvalRows[0];
    return enrichFmeaReview(review, cases);
  }, [approvalRows, selectedReviewId, fmeaReviews, cases]);
  const mayApproveSelectedReview = selectedReview
    ? selectedReview.review.approvalPersonas
      ? selectedReview.review.approvalPersonas.includes(persona)
      : can('fmea:approve')
    : false;

  useEffect(() => {
    if (approvalRows.length === 0) {
      setSelectedReviewId(null);
      return;
    }
    if (reviewParam && approvalRows.some((row) => row.review.id === reviewParam)) {
      if (selectedReviewId !== reviewParam) {
        setSelectedReviewId(reviewParam);
      }
      return;
    }
    if (!selectedReviewId || !approvalRows.some((row) => row.review.id === selectedReviewId)) {
      setSelectedReviewId(approvalRows[0]?.review.id ?? null);
    }
  }, [approvalRows, selectedReviewId, reviewParam]);

  useEffect(() => {
    if (stage !== 'learn' || !selectedReviewId) return;
    const row = document.querySelector<HTMLElement>(`[data-review-id="${selectedReviewId}"]`);
    const scroller = row?.closest<HTMLElement>('[data-approvals-scroll]');
    if (!row || !scroller) return;
    const rowRect = row.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    if (rowRect.top < scrollerRect.top) scroller.scrollTop -= scrollerRect.top - rowRect.top;
    else if (rowRect.bottom > scrollerRect.bottom) scroller.scrollTop += rowRect.bottom - scrollerRect.bottom;
  }, [stage, selectedReviewId, approvalRows.length]);

  const openCasePage = (caseId: string) => {
    const from = `${location.pathname}${location.search}`;
    navigate(`/projects/${projectId ?? 'alpha'}/advisory/case/${caseId}`, { state: { from } });
  };

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
          <Link to="/projects" className="text-sm font-medium text-accent-700 dark:text-accent-400">
            Back to portfolio
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  if (!flowRead) {
    return (
      <SurfacePageLayout width="full">
        <Commentable name="AdvisoryScene">
          <SurfacePageBody className="space-y-2" width="full">
            <FlowLanding
              stage={stage}
              cells={NEUTRAL_CELLS}
              valueRead={valueRead}
              projectName={project.name}
              cursor={state.cursor}
              playing={false}
              onToggleReplay={() => undefined}
              onScrub={() => undefined}
              onSelectStage={changeStage}
              showMoney={showMoney}
            >
              <SurfacePagePanel title={PIPELINE_STAGES.find((row) => row.id === stage)?.label ?? 'Advisory'}>
                <div className="h-24" />
              </SurfacePagePanel>
            </FlowLanding>
          </SurfacePageBody>
        </Commentable>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="full">
      <Commentable name="AdvisoryScene">
        <SurfacePageBody className="space-y-2" width="full">
          <FlowLanding
            stage={stage}
            cells={flowRead.cells}
            valueRead={valueRead}
            projectName={project.name}
            cursor={state.cursor}
            playing={replayPlaying}
            holding={replayHolding}
            onToggleReplay={toggleReplay}
            onScrub={scrubReplay}
            markers={flowRead.markers}
            arrivingStage={arrivingStage}
            replayTicks={replayTicks}
            onSelectStage={changeStage}
            showMoney={showMoney}
          >
          {stage === 'plant-data' ? (
            <PlantDataBody rows={flowRead.plantDataRows} subprocessName={subprocessName} />
          ) : null}

          {stage === 'signals' ? (
            <SignalsBody
              read={flowRead}
              cases={pipelineCases}
              alarms={dcsAlarms}
              cursorLabel={(raisedAt) => formatCaseClock(raisedAt, state.cursor)}
              canAck={can('alarm:ack')}
              onAck={(alarmId) => advisory.ackAlarm(alarmId)}
              onOpenCase={openCasePage}
            />
          ) : null}

          {stage === 'excursions' ? (
            <ExcursionsBody
              read={flowRead}
              cases={pipelineCases}
              alarms={dcsAlarms}
              tagMappings={TAG_MAPPING}
              windowStart={ZINC_REPLAY_START}
              cursor={state.cursor}
              onOpenCase={openCasePage}
            />
          ) : null}

          {stage === 'cases' ? (
            <Commentable name="Queue">
              <div className="space-y-3">
                <CasesFlowPanels
                  read={flowRead}
                  projectPath={`/projects/${projectId}`}
                  caseCodeById={caseCodeById}
                  trackedCaseId={replayCaseId && zincStage ? replayCaseId : null}
                  showMoney={showMoney}
                  onOpenCase={openCasePage}
                />
              <RoleScreen
                model={roleScreenModel}
                cases={displayCases}
                actions={actions}
                projectPath={`/projects/${projectId}`}
                sessionState={state}
                activeScenarios={state.scenarios}
                showScenarioToggle={false}
                canApproveFmea={can('fmea:approve')}
                onOpenCase={openCasePage}
                onApproveReview={(reviewId) => advisory.approveChange(reviewId)}
              />
              </div>
            </Commentable>
          ) : null}

          {stage === 'diagnosis' ? (
            <Commentable name="Diagnosis">
              <div>
                <DiagnosisBody
                  read={flowRead}
                  runs={flowRead.investigationRuns}
                  projectId={projectId ?? 'alpha'}
                  onOpenCase={openCasePage}
                />
              </div>
            </Commentable>
          ) : null}

          {stage === 'value' ? (
            <Commentable name="Value">
              <div>
                <ValueBody
                  capturedRows={flowRead.capturedRows}
                  showMoney={showMoney}
                  onOpenCase={openCasePage}
                />
              </div>
            </Commentable>
          ) : null}

          {stage === 'action' ? (
            <ActionBody
              read={flowRead}
              actions={actions}
              showMoney={showMoney}
              onOpenCase={openCasePage}
            />
          ) : null}

          {stage === 'learn' ? (
            <LearnBody
              ruleSet={learnRuleSet}
              approvalPanel={approvalPanel}
              learnedRows={flowRead.learnedRows}
              reviews={fmeaReviews}
              approvalTab={approvalTab}
              onApprovalTab={setApprovalTab}
              approvalRows={approvalRows}
              selectedReview={selectedReview}
              onSelectReview={setSelectedReviewId}
              stateLabel={(review) => learnStateLabel(review, state.cursor, learnRuleSet.version)}
              reviewClock={(stamp) => eventClock(stamp, state.cursor)}
              showMoney={showMoney}
              gate={{
                persona,
                canReview: can('fmea:review'),
                mayApprove: mayApproveSelectedReview,
                defaultGate: APPROVAL_GATE_SEAT,
                onReview: (reviewId, notes) => advisory.reviewChange(reviewId, notes),
                onApprove: (reviewId) => advisory.approveChange(reviewId),
                onReject: (reviewId, reason) => advisory.rejectChange(reviewId, reason),
              }}
              ruleSetBody={(
                <RuleSetPanel
                  advisory={advisory}
                  cases={casesForDisplay}
                  diagnosis={diagnosis}
                  highlightCauseId={causeParam ?? undefined}
                />
              )}
              sessionUpdates={fmeaLearningModes(advisory.fmeaLearning).length}
              sessionBody={<FmeaLearningUpdates state={advisory.fmeaLearning} bare />}
              onOpenCase={openCasePage}
            />
          ) : null}
          </FlowLanding>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
